import React, { useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  MESH_NODE_COLOR,
  MESH_NODE_BRIGHT,
  MESH_SCAN_CORE,
  MESH_EDGE_ALPHA,
  MESH_NODE_RADIUS_FRAC,
  MESH_PULSE_PERIOD_MS,
  MESH_DPR_CAP,
  REVEAL_DURATION_MS,
  REVEAL_IGNITE_MS,
  SHIMMER_CENTERS,
  SHIMMER_RADIUS,
  NODE_DIM_ALPHA,
  NODE_BRIGHT_ALPHA,
  NODE_GROW,
  NODE_REVEAL_ALPHA,
  EDGE_DIM_ALPHA,
  EDGE_BRIGHT_ALPHA,
  GLOW_SPRITE_SCALE,
  DEPTH_GAIN_MIN,
  DEPTH_GAIN_MAX,
  WRAPPER_FADE_MS,
} from '../../lib/facePrep/constants';

// Aurum face-mesh overlay. DESIGN.md §6.1 FaceMeshOverlay + §16.2 ANALYZING.
//
// Two modes:
//
// 1. LANDMARK mode — `landmarks` (+ `edges`) provided: the REAL face mesh,
//    detected in-browser by src/lib/facePrep during upload normalization.
//    Landmarks are output-space fractions (x, y in 0..1 of the 3:4 frame,
//    z = MediaPipe relative depth); the normalized portrait is exactly 3:4,
//    so fractions map 1:1 onto the container. One <canvas> + rAF:
//
//      mount ─▶ REVEAL (0..1200ms): Aurum scan-line sweeps top→bottom;
//               nodes/edges ignite as the line passes (200ms alpha ramp)
//            ─▶ LOOP: roaming illumination — invisible shimmer lights wander
//               the face on deterministic Lissajous paths; points AND edges
//               near a light brighten (gradient glow, nodes grow), the rest
//               of the mesh sits dark; nearer features (smaller z) get a
//               small extra gain
//
// 2. DECORATIVE mode — no landmarks: ~80 deterministic seeded Aurum dots in
//    an elliptical face region with nearest-neighbor hairlines. Marketing
//    surfaces (Sign-In hero, Splash, Vault focus) and the ANALYZING fallback
//    when face detection didn't produce landmarks. "Not a real face-landmark
//    visualization in marketing contexts" (§6.1).
//
// Props:
//   landmarks: [{x,y,z}]   output-space fractions → LANDMARK mode
//   edges: [[a,b]]         tessellation index pairs (LANDMARK mode)
//   density: number        approx decorative dot count (default 80)
//   showLines: bool        decorative connecting hairlines (default true)
//   pulse: bool            decorative mesh-pulse animation (default true)
//   scanline: bool         decorative gold scan-line (decorative mode only)
//   subtle: bool           quieter LANDMARK-mode tone for marketing surfaces
//   pinNodes: [int]        indices of the dots to draw (even-density subset);
//                          edges still span the full mesh (subtle mode)
//   pointerRef: ref        { fx, fy, active, interactive } in container
//                          fractions — drives the cursor-following highlight
//                          in subtle mode (sign-up hero). Null = wander only.
//   className: string

// ---- LANDMARK mode pure helpers (exported for unit tests) ----

// Per-node depth gain: nearest landmark (min z) → DEPTH_GAIN_MAX, farthest →
// DEPTH_GAIN_MIN. Flat-depth input (z range 0) → all 1.
export function computeDepthGains(landmarks) {
  let zMin = Infinity;
  let zMax = -Infinity;
  landmarks.forEach((lm) => {
    zMin = Math.min(zMin, lm.z);
    zMax = Math.max(zMax, lm.z);
  });
  const range = zMax - zMin;
  return landmarks.map((lm) => {
    if (!range) return 1;
    const nearness = (zMax - lm.z) / range; // 1 = nearest
    return DEPTH_GAIN_MIN + (DEPTH_GAIN_MAX - DEPTH_GAIN_MIN) * nearness;
  });
}

// Deterministic 32-bit hash → [0, 1). Same input → same output, so the
// "random" searching pattern is stable across renders and testable.
export function hash01(n) {
  let h = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

// Positions of the roaming shimmer lights at time tMs, in output-frame
// fractions. Each light wanders a sum-of-sines (Lissajous) path with
// hash-seeded phases — smooth, erratic-looking, deterministic, and bounded
// to the face region (x 0.5±0.32, y 0.45±0.38).
export function shimmerCenters(tMs, count = SHIMMER_CENTERS) {
  const out = [];
  const w = (2 * Math.PI * tMs) / MESH_PULSE_PERIOD_MS;
  for (let c = 0; c < count; c += 1) {
    const p1 = hash01(c * 101 + 13) * 2 * Math.PI;
    const p2 = hash01(c * 211 + 17) * 2 * Math.PI;
    const p3 = hash01(c * 307 + 19) * 2 * Math.PI;
    const p4 = hash01(c * 401 + 23) * 2 * Math.PI;
    out.push({
      x: 0.5 + 0.22 * Math.sin(w * 0.9 + p1) + 0.1 * Math.sin(w * 2.3 + p2),
      y: 0.45 + 0.26 * Math.sin(w * 0.7 + p3) + 0.12 * Math.sin(w * 1.9 + p4),
    });
  }
  return out;
}

// Highlight intensity of a point under the shimmer lights: gaussian falloff
// of distance to the NEAREST light (max, not sum — overlapping lights don't
// over-brighten). 1 at a light's center, →0 in the dark.
export function highlightAt(p, centers, sigma = SHIMMER_RADIUS) {
  let h = 0;
  centers.forEach((c) => {
    const d2 = (p.x - c.x) ** 2 + (p.y - c.y) ** 2;
    h = Math.max(h, Math.exp(-d2 / (2 * sigma * sigma)));
  });
  return h;
}

// Node styling across the dark→lit range: alpha DIM→BRIGHT, radius grows to
// grow× at full highlight and shrinks back as the light moves away. `tone`
// overrides support the subtle marketing variant; defaults match the scan
// flow exactly.
export function nodeStyle(h, depthGain, tone = {}) {
  const dim = tone.nodeDimAlpha != null ? tone.nodeDimAlpha : NODE_DIM_ALPHA;
  const bright = tone.nodeBrightAlpha != null ? tone.nodeBrightAlpha : NODE_BRIGHT_ALPHA;
  const grow = tone.nodeGrow != null ? tone.nodeGrow : NODE_GROW;
  return {
    alpha: (dim + (bright - dim) * h) * depthGain,
    radiusScale: (1 + (grow - 1) * h) * depthGain,
  };
}

// Marketing register (sign-in hero): "pins of light" — champagne nodes far
// brighter than the lines, a soft additive bloom behind, thin dim lines.
// Drives the dedicated subtle renderer (drawSubtleFrame), not the scan-flow
// nodeStyle path. The scan flow keeps the full-intensity defaults.
export const SUBTLE_TONE = Object.freeze({
  lineWidth: 0.55, // thin
  lineAlphaMin: 0.2, // lines: 20–35% opacity band
  lineAlphaMax: 0.34,
  nodeAlphaMin: 0.7, // nodes: 70–100% opacity
  nodeAlphaMax: 1,
  nodeRadiusFrac: 0.003, // pin half-size as a fraction of width (all 478 nodes)
  shimmerSigma: 0.15,
  bloomBlur: 5, // blurred-duplicate bloom radius (CSS px)
  bloomAlpha: 0.3, // bloom layer 25–45% opacity
});

// Cursor repulsion field (subtle interactive mode): dots are pushed away
// from the cursor with a gaussian falloff and ease toward/back from their
// rest position each frame — gentle antigravity, never a snap.
const REPEL_STRENGTH = 0.011; // max dot travel, fraction of box width
const REPEL_SIGMA = 0.12; // field radius, fraction of box width
const REPEL_EASE = 0.12; // per-frame ease toward the target offset

// REVEAL-phase ignition: 0 before the sweep line crosses the node's y, then
// ramps to 1 over REVEAL_IGNITE_MS.
export function revealIgnite(tMs, yFrac) {
  const crossAt = yFrac * REVEAL_DURATION_MS;
  if (tMs <= crossAt) return 0;
  return Math.min(1, (tMs - crossAt) / REVEAL_IGNITE_MS);
}

const inFrame = (lm) => lm.x >= 0 && lm.x <= 1 && lm.y >= 0 && lm.y <= 1;

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ---- LANDMARK mode (canvas) ----

const LandmarkMesh = ({
  landmarks,
  edges,
  subtle = false,
  pinNodes = null,
  pointerRef = null,
  className,
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined; // jsdom safety — render nothing

    const tone = subtle ? SUBTLE_TONE : {};
    const sigma = tone.shimmerSigma != null ? tone.shimmerSigma : SHIMMER_RADIUS;
    const edgeDim = tone.edgeDimAlpha != null ? tone.edgeDimAlpha : EDGE_DIM_ALPHA;
    const edgeBright = tone.edgeBrightAlpha != null ? tone.edgeBrightAlpha : EDGE_BRIGHT_ALPHA;
    const revealAlpha = tone.revealAlpha != null ? tone.revealAlpha : NODE_REVEAL_ALPHA;

    // Subtle (marketing) renderer parameters — pins of light + additive bloom.
    const lineWidth = tone.lineWidth != null ? tone.lineWidth : 1;
    const lineAlphaMin = tone.lineAlphaMin != null ? tone.lineAlphaMin : edgeDim;
    const lineAlphaMax = tone.lineAlphaMax != null ? tone.lineAlphaMax : edgeBright;
    const nodeAlphaMin = tone.nodeAlphaMin != null ? tone.nodeAlphaMin : NODE_DIM_ALPHA;
    const nodeAlphaMax = tone.nodeAlphaMax != null ? tone.nodeAlphaMax : NODE_BRIGHT_ALPHA;
    const bloomBlur = tone.bloomBlur != null ? tone.bloomBlur : 5;
    const bloomAlpha = tone.bloomAlpha != null ? tone.bloomAlpha : 0.38;

    const dpr = Math.min(window.devicePixelRatio || 1, MESH_DPR_CAP);
    const depthGains = computeDepthGains(landmarks);
    const reduced = prefersReducedMotion();

    // The in-frame, finite subset never changes between frames — precompute it
    // once so the rAF loop skips re-testing every node/edge (and a NaN coord
    // can't leak into globalAlpha). Endpoints of a kept edge are always kept
    // nodes, so hs[a]/hs[b] are always populated below.
    const finite = (lm) =>
      lm && Number.isFinite(lm.x) && Number.isFinite(lm.y);
    const nodeIdx = [];
    landmarks.forEach((lm, i) => {
      if (finite(lm) && inFrame(lm)) nodeIdx.push(i);
    });
    const edgeList = (edges || []).filter(([a, b]) => {
      const pa = landmarks[a];
      const pb = landmarks[b];
      return pa && pb && finite(pa) && finite(pb) && inFrame(pa) && inFrame(pb);
    });
    // Pins to draw: the decimated even-density subset when provided (the
    // hero), every in-frame node otherwise. Edges always span the full mesh.
    const pinIdx = pinNodes
      ? pinNodes.filter((i) => {
          const lm = landmarks[i];
          return lm && finite(lm) && inFrame(lm);
        })
      : nodeIdx;
    // Reused per-frame buffers — no allocation inside the rAF loop.
    const hs = new Float32Array(landmarks.length);
    // Cursor-repulsion displacement (output-frame fractions), eased per frame.
    const dispX = new Float32Array(landmarks.length);
    const dispY = new Float32Array(landmarks.length);

    let rafId = null;
    let startMs = null;
    let cssW = 0;
    let cssH = 0;
    let meshCanvas = null; // offscreen sharp mesh; composited with 'lighter' each frame
    let mctx = null; // cached 2D context of meshCanvas (set in resize())
    // Smoothed pointer-light position (subtle interactive mode) — lerps toward
    // the cursor while it moves, drifts on the idle wander path otherwise.
    let smX = 0.5;
    let smY = 0.45;

    // Pre-rendered radial-gradient glow sprites (bright core falling off to
    // transparent). One drawImage per node — never createRadialGradient
    // inside the rAF loop.
    const SPRITE_PX = 64;
    const buildSprite = (color) => {
      const sprite = document.createElement('canvas');
      sprite.width = SPRITE_PX;
      sprite.height = SPRITE_PX;
      const sctx = sprite.getContext('2d');
      if (!sctx || typeof sctx.createRadialGradient !== 'function') return null;
      const half = SPRITE_PX / 2;
      const grad = sctx.createRadialGradient(half, half, 0, half, half, half);
      grad.addColorStop(0, color);
      grad.addColorStop(0.2, color);
      grad.addColorStop(0.45, `${color}55`); // hue fades out, never grey
      grad.addColorStop(1, `${color}00`);
      sctx.fillStyle = grad;
      sctx.fillRect(0, 0, SPRITE_PX, SPRITE_PX);
      return sprite;
    };
    const spriteDim = buildSprite(MESH_NODE_COLOR);
    const spriteBright = buildSprite(MESH_NODE_BRIGHT);

    // Pin-of-light sprite (subtle marketing register): a tight pale-champagne
    // core → amber → shadow-gold falloff to transparent. No broad glow — the
    // separate blurred bloom pass supplies the halo.
    const buildPinSprite = () => {
      const S = 48;
      const sprite = document.createElement('canvas');
      sprite.width = S;
      sprite.height = S;
      const sctx = sprite.getContext('2d');
      if (!sctx || typeof sctx.createRadialGradient !== 'function') return null;
      const half = S / 2;
      const grad = sctx.createRadialGradient(half, half, 0, half, half, half);
      grad.addColorStop(0, '#FFF6E0'); // hot core
      grad.addColorStop(0.24, '#F8D98A'); // pale champagne
      grad.addColorStop(0.52, '#C99A42'); // amber glow
      grad.addColorStop(1, 'rgba(122,90,36,0)'); // shadow gold → transparent
      sctx.fillStyle = grad;
      sctx.fillRect(0, 0, S, S);
      return sprite;
    };
    const pinSprite = subtle ? buildPinSprite() : null;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      cssW = rect.width || canvas.clientWidth;
      cssH = rect.height || canvas.clientHeight;
      if (!cssW || !cssH) return;
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      // Sharp mesh rendered into meshCanvas each frame, then composited
      // (blurred bloom + sharp) onto the visible canvas with 'lighter'.
      meshCanvas = document.createElement('canvas');
      meshCanvas.width = canvas.width;
      meshCanvas.height = canvas.height;
      mctx = meshCanvas.getContext('2d');
    };

    const drawScanLine = (sweepY) => {
      // Layered Aurum band — matches the decorative scanline treatment:
      // soft wide band, bright core line, warm inner highlight.
      ctx.fillStyle = MESH_NODE_COLOR;
      ctx.globalAlpha = 0.18;
      ctx.fillRect(0, sweepY - 4, cssW, 9);
      ctx.fillStyle = MESH_NODE_BRIGHT;
      ctx.globalAlpha = 0.9;
      ctx.fillRect(0, sweepY, cssW, 1.5);
      ctx.fillStyle = MESH_SCAN_CORE;
      ctx.globalAlpha = 0.6;
      ctx.fillRect(cssW * 0.32, sweepY + 0.25, cssW * 0.36, 1);
    };

    const drawFrame = (tMs) => {
      if (!cssW || !cssH || !mctx) return;
      const revealing = !reduced && tMs < REVEAL_DURATION_MS;
      const sweepY = revealing ? (tMs / REVEAL_DURATION_MS) * cssH : cssH;

      mctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      mctx.clearRect(0, 0, cssW, cssH);

      const r0 = (tone.nodeRadiusFrac != null ? tone.nodeRadiusFrac : MESH_NODE_RADIUS_FRAC) * cssW;
      const centers = !reduced && !revealing ? shimmerCenters(tMs - REVEAL_DURATION_MS) : null;
      for (let n = 0; n < nodeIdx.length; n += 1) {
        const i = nodeIdx[n];
        // eslint-disable-next-line no-nested-ternary
        hs[i] = reduced ? 0.35 : revealing ? 0 : highlightAt(landmarks[i], centers, sigma);
      }

      // Dim edge baseline
      mctx.strokeStyle = MESH_NODE_COLOR;
      mctx.globalAlpha = edgeDim;
      mctx.lineWidth = 1;
      mctx.beginPath();
      edgeList.forEach(([a, b]) => {
        mctx.moveTo(landmarks[a].x * cssW, landmarks[a].y * cssH);
        mctx.lineTo(landmarks[b].x * cssW, landmarks[b].y * cssH);
      });
      mctx.stroke();

      // Bright edges inside the shimmer zone
      if (!revealing) {
        mctx.strokeStyle = MESH_NODE_BRIGHT;
        mctx.lineWidth = 1;
        edgeList.forEach(([a, b]) => {
          const hE = (hs[a] + hs[b]) / 2;
          if (hE < 0.04) return;
          mctx.globalAlpha = (edgeBright - edgeDim) * hE;
          mctx.beginPath();
          mctx.moveTo(landmarks[a].x * cssW, landmarks[a].y * cssH);
          mctx.lineTo(landmarks[b].x * cssW, landmarks[b].y * cssH);
          mctx.stroke();
        });
      }

      // Node glow sprites
      for (let n = 0; n < nodeIdx.length; n += 1) {
        const i = nodeIdx[n];
        const lm = landmarks[i];
        let alpha;
        let radius;
        if (revealing) {
          const ignite = revealIgnite(tMs, lm.y);
          if (!ignite) continue; // eslint-disable-line no-continue
          alpha = revealAlpha * depthGains[i] * ignite;
          radius = r0 * depthGains[i];
        } else {
          const style = nodeStyle(hs[i], depthGains[i], tone);
          alpha = style.alpha;
          radius = r0 * style.radiusScale;
        }
        if (spriteDim) {
          const x = lm.x * cssW;
          const y = lm.y * cssH;
          const size = radius * GLOW_SPRITE_SCALE;
          mctx.globalAlpha = Math.max(0, Math.min(1, alpha));
          mctx.drawImage(spriteDim, x - size, y - size, size * 2, size * 2);
          if (hs[i] > 0.05 && spriteBright) {
            mctx.globalAlpha = Math.max(0, Math.min(1, alpha * hs[i]));
            mctx.drawImage(spriteBright, x - size, y - size, size * 2, size * 2);
          }
        }
      }
      mctx.globalAlpha = 1;

      // Composite: bloom (blurred duplicate) + sharp, both additive ('lighter'
      // = linear-dodge). Overlapping glow sprites and bright edges accumulate
      // brightness instead of occluding — same blend as the sign-in hero.
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'lighter';
      ctx.save();
      if (revealing) {
        ctx.beginPath();
        ctx.rect(0, 0, canvas.width, sweepY * dpr);
        ctx.clip();
      }
      ctx.filter = `blur(${bloomBlur * dpr}px)`;
      ctx.globalAlpha = bloomAlpha;
      ctx.drawImage(meshCanvas, 0, 0);
      ctx.filter = 'none';
      ctx.globalAlpha = 1;
      ctx.drawImage(meshCanvas, 0, 0);
      ctx.restore();
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;

      if (revealing) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        drawScanLine(sweepY);
      }
    };

    // Subtle marketing renderer — "pins of light" with an additive bloom.
    //   1. render the sharp mesh (thin amber lines + champagne pin nodes) into
    //      an offscreen canvas
    //   2. composite onto the visible canvas with 'lighter' (linear-dodge):
    //      a blurred duplicate (bloom) behind, the sharp mesh on top
    // Node alpha runs hot (0.7–1) while lines stay dim (0.2–0.34), so nodes
    // read as bright points and the lines as faint connective tissue.
    const drawSubtleFrame = (tMs) => {
      if (!cssW || !cssH || !mctx || !pinSprite) return;
      const revealing = !reduced && tMs < REVEAL_DURATION_MS;
      const sweepY = revealing ? (tMs / REVEAL_DURATION_MS) * cssH : cssH;

      mctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      mctx.clearRect(0, 0, cssW, cssH);

      // Interactive (sign-up) mode: a single highlight follows the cursor
      // while it moves, and drifts on the wander path when the cursor is
      // idle. Non-interactive mode keeps the deterministic 2-light shimmer.
      const p = pointerRef && pointerRef.current;
      const usePointer = !!(p && p.interactive) && !reduced && !revealing;
      let centers;
      if (usePointer) {
        const idle = !p.active;
        const wander = idle ? shimmerCenters(tMs - REVEAL_DURATION_MS, 1)[0] : null;
        const tX = idle ? wander.x : p.fx;
        const tY = idle ? wander.y : p.fy;
        const k = idle ? 0.05 : 0.16;
        smX += (tX - smX) * k;
        smY += (tY - smY) * k;
        centers = [{ x: smX, y: smY }];
      } else {
        centers =
          !reduced && !revealing ? shimmerCenters(tMs - REVEAL_DURATION_MS) : null;
      }
      for (let n = 0; n < nodeIdx.length; n += 1) {
        const i = nodeIdx[n];
        // eslint-disable-next-line no-nested-ternary
        hs[i] = reduced ? 0.45 : revealing ? 0.6 : highlightAt(landmarks[i], centers, sigma);
      }

      // Cursor repulsion — each dot is gently pushed away from the live
      // cursor (gaussian falloff, aspect-corrected so the field is circular
      // on screen) and eases back when the cursor leaves or goes idle. Only
      // the DOTS move; the portrait underneath stays fixed.
      const aspect = cssH / cssW; // y-fractions cover more px in a 3:4 box
      const repelOn = usePointer && p.active;
      for (let n = 0; n < nodeIdx.length; n += 1) {
        const i = nodeIdx[n];
        let txd = 0;
        let tyd = 0;
        if (repelOn) {
          const lm = landmarks[i];
          const dx = lm.x - p.fx;
          const dy = (lm.y - p.fy) * aspect;
          const d2 = dx * dx + dy * dy;
          const d = Math.sqrt(d2) || 0.0001;
          const mag = REPEL_STRENGTH * Math.exp(-d2 / (2 * REPEL_SIGMA * REPEL_SIGMA));
          txd = (dx / d) * mag;
          tyd = ((dy / d) * mag) / aspect;
        }
        dispX[i] += (txd - dispX[i]) * REPEL_EASE;
        dispY[i] += (tyd - dispY[i]) * REPEL_EASE;
      }

      // Thin lines — drawn between the DISPLACED dot positions so the web
      // flexes coherently with the repulsion.
      mctx.strokeStyle = '#C99A42';
      mctx.lineWidth = lineWidth;
      edgeList.forEach(([a, b]) => {
        const hE = (hs[a] + hs[b]) / 2;
        mctx.globalAlpha = lineAlphaMin + (lineAlphaMax - lineAlphaMin) * hE;
        mctx.beginPath();
        mctx.moveTo((landmarks[a].x + dispX[a]) * cssW, (landmarks[a].y + dispY[a]) * cssH);
        mctx.lineTo((landmarks[b].x + dispX[b]) * cssW, (landmarks[b].y + dispY[b]) * cssH);
        mctx.stroke();
      });

      // Bright pin nodes — the decimated even-density subset only.
      const pr0 = (tone.nodeRadiusFrac != null ? tone.nodeRadiusFrac : MESH_NODE_RADIUS_FRAC) * cssW;
      for (let n = 0; n < pinIdx.length; n += 1) {
        const i = pinIdx[n];
        const lm = landmarks[i];
        const h = hs[i];
        const size = pr0 * (1 + 0.4 * h) * depthGains[i];
        mctx.globalAlpha = Math.min(
          1,
          (nodeAlphaMin + (nodeAlphaMax - nodeAlphaMin) * h) * depthGains[i],
        );
        mctx.drawImage(
          pinSprite,
          (lm.x + dispX[i]) * cssW - size,
          (lm.y + dispY[i]) * cssH - size,
          size * 2,
          size * 2,
        );
      }
      mctx.globalAlpha = 1;

      // Composite: bloom (blurred duplicate) then sharp, both additive.
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'lighter';
      ctx.save();
      if (revealing) {
        ctx.beginPath();
        ctx.rect(0, 0, canvas.width, sweepY * dpr);
        ctx.clip();
      }
      ctx.filter = `blur(${bloomBlur * dpr}px)`;
      ctx.globalAlpha = bloomAlpha;
      ctx.drawImage(meshCanvas, 0, 0);
      ctx.filter = 'none';
      ctx.globalAlpha = 1;
      ctx.drawImage(meshCanvas, 0, 0);
      ctx.restore();
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;

      if (revealing) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        drawScanLine(sweepY);
      }
    };

    const renderFrame = subtle ? drawSubtleFrame : drawFrame;

    const tick = (now) => {
      if (startMs === null) startMs = now;
      renderFrame(now - startMs);
      rafId = requestAnimationFrame(tick);
    };

    const onVisibility = () => {
      if (document.hidden) {
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = null;
      } else if (rafId === null && !reduced) {
        rafId = requestAnimationFrame(tick);
      }
    };

    resize();
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    if (observer) observer.observe(canvas);

    if (reduced) {
      renderFrame(0); // single static frame, no loop
    } else {
      rafId = requestAnimationFrame(tick);
      document.addEventListener('visibilitychange', onVisibility);
    }

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (observer) observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [landmarks, edges, subtle, pinNodes, pointerRef]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ type: 'tween', duration: WRAPPER_FADE_MS / 1000 }}
      className={['pointer-events-none', className].filter(Boolean).join(' ')}
    >
      <canvas
        ref={canvasRef}
        data-testid="face-mesh-overlay"
        aria-hidden="true"
        className="w-full h-full"
      />
    </motion.div>
  );
};

// ---- DECORATIVE mode (seeded SVG) ----

// Deterministic pseudo-random — same seed → same coordinates each render.
// We avoid Math.random so SSR / snapshot tests stay stable.
function seedPoints(count) {
  const points = [];
  let s = 1;
  const next = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  // Cluster points within an elliptical face region so the overlay reads as
  // landmark-shaped, not a uniform grid.
  for (let i = 0; i < count; i += 1) {
    // polar within ellipse
    const theta = next() * Math.PI * 2;
    const r = Math.sqrt(next());
    const cx = 50 + Math.cos(theta) * 32 * r;
    const cy = 62 + Math.sin(theta) * 48 * r * 0.85;
    points.push({ x: Number(cx.toFixed(2)), y: Number(cy.toFixed(2)) });
  }
  return points;
}

const DecorativeMesh = ({ density, showLines, pulse, scanline, className }) => {
  const points = useMemo(() => seedPoints(density), [density]);

  // Build a sparse adjacency: each point connects to its 2 nearest neighbors.
  // O(n^2) is fine at n≈80.
  const lines = useMemo(() => {
    if (!showLines) return [];
    const edges = [];
    points.forEach((p, i) => {
      const distances = points
        .map((q, j) => ({ j, d: i === j ? Infinity : (p.x - q.x) ** 2 + (p.y - q.y) ** 2 }))
        .sort((a, b) => a.d - b.d)
        .slice(0, 2);
      distances.forEach(({ j }) => {
        if (j > i) edges.push({ a: p, b: points[j] });
      });
    });
    return edges;
  }, [points, showLines]);

  return (
    <svg
      viewBox="0 0 100 125"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      className={[
        'pointer-events-none',
        pulse ? 'motion-safe:animate-mesh-pulse' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {lines.map((edge, i) => (
        <line
          key={`l-${i}`}
          x1={edge.a.x}
          y1={edge.a.y}
          x2={edge.b.x}
          y2={edge.b.y}
          stroke={MESH_NODE_COLOR}
          strokeOpacity={MESH_EDGE_ALPHA}
          strokeWidth="0.2"
        />
      ))}
      {points.map((p, i) => (
        <circle
          key={`d-${i}`}
          cx={p.x}
          cy={p.y}
          r="0.7"
          fill={MESH_NODE_COLOR}
        />
      ))}
      {scanline && (
        // Horizontal gold glow line at the eye-line (mobile Vault mockup).
        <g className="motion-safe:animate-scanline">
          <rect x="0" y="46.4" width="100" height="1.4" fill={MESH_NODE_COLOR} opacity="0.18" />
          <rect x="0" y="46.9" width="100" height="0.45" fill={MESH_NODE_BRIGHT} opacity="0.9" />
          <rect x="32" y="46.7" width="36" height="0.9" fill={MESH_SCAN_CORE} opacity="0.6" />
        </g>
      )}
    </svg>
  );
};

// ---- Mode switch ----

const FaceMeshOverlay = ({
  landmarks = null,
  edges = null,
  density = 80,
  showLines = true,
  pulse = true,
  scanline = false,
  subtle = false,
  pinNodes = null,
  pointerRef = null,
  className = '',
}) => {
  if (landmarks && landmarks.length) {
    return (
      <LandmarkMesh
        landmarks={landmarks}
        edges={edges}
        subtle={subtle}
        pinNodes={pinNodes}
        pointerRef={pointerRef}
        className={className}
      />
    );
  }
  return (
    <DecorativeMesh
      density={density}
      showLines={showLines}
      pulse={pulse}
      scanline={scanline}
      className={className}
    />
  );
};

export default FaceMeshOverlay;
